import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail } from 'lucide-react';
import type { TeamMemberRole } from '@arrowise/shared';

interface InviteMemberDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    email: string;
    onEmailChange: (email: string) => void;
    role: TeamMemberRole;
    onRoleChange: (role: TeamMemberRole) => void;
    isInviting: boolean;
    onInvite: () => void;
    isOwner?: boolean;
    trigger?: React.ReactNode;
}

export const InviteMemberDialog: React.FC<InviteMemberDialogProps> = ({
    open,
    onOpenChange,
    email,
    onEmailChange,
    role,
    onRoleChange,
    isInviting,
    onInvite,
    isOwner = false,
    trigger,
}) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {trigger && (
                <DialogTrigger asChild>
                    {trigger}
                </DialogTrigger>
            )}
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Invite Team Member</DialogTitle>
                    <DialogDescription>
                        Send an invitation to join your team.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-6 py-4">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-800">
                            <strong>Note:</strong> The user must already be registered and verified with this email address before you can send the invite.
                        </p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="invite-email">Email Address</Label>
                        <Input
                            id="invite-email"
                            type="email"
                            value={email}
                            onChange={(e) => onEmailChange(e.target.value)}
                            placeholder="user@example.com"
                            className="mt-1"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="invite-role">Role</Label>
                        <Select value={role} onValueChange={(value: TeamMemberRole) => onRoleChange(value)}>
                            <SelectTrigger className="mt-1 cursor-pointer">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="member" className="cursor-pointer">Member</SelectItem>
                                {isOwner && <SelectItem value="admin" className="cursor-pointer">Admin</SelectItem>}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={onInvite} disabled={isInviting}>
                        <Mail />
                        {isInviting ? 'Sending...' : 'Send Invitation'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
